import React, { useState, useEffect } from 'react';
import {
    useRefinementList,
    useRange,
    useCurrentRefinements,
    useClearRefinements,
    useSortBy,
    useSearchBox
} from 'react-instantsearch';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { getAttributesForCategory } from '../lib/categories';

// Helper to format currency in TRY
const formatPrice = (value: number) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(value);
};

// --- Pill Chip Component ---
const PillChip = ({ label, count, isActive, onClick }: { label: string, count: number, isActive: boolean, onClick: () => void }) => (
    <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={onClick}
        className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all flex items-center gap-1.5 ${isActive
            ? 'bg-gray-950 dark:bg-white text-white dark:text-gray-950 shadow-sm'
            : 'bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 text-gray-500 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-600 hover:text-gray-900 dark:hover:text-white'
            }`}
    >
        <span>{label}</span>
        <span className={`text-[10px] ${isActive ? 'text-white/70 dark:text-gray-950/70' : 'text-gray-300 dark:text-gray-700'}`}>({count})</span>
    </motion.button>
);

// --- Dual Thumb Price Slider ---
const PriceRangeSlider = () => {
    const { start, range, canRefine, refine } = useRange({ attribute: 'price' });
    const { t } = useTranslation('marketplace');

    const [localMin, setLocalMin] = useState(start[0] ?? range.min ?? 0);
    const [localMax, setLocalMax] = useState(start[1] ?? range.max ?? 100000);

    const minBound = range.min ?? 0;
    const maxBound = range.max ?? 100000;

    // Sync with Algolia state
    useEffect(() => {
        setLocalMin(start[0] ?? range.min ?? 0);
        setLocalMax(start[1] ?? range.max ?? 100000);
    }, [start, range.min, range.max]);

    const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = Number(e.target.value);
        setLocalMin(Math.min(val, localMax - 100));
    };

    const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = Number(e.target.value);
        setLocalMax(Math.max(val, localMin + 100));
    };

    const handleApply = () => {
        refine([localMin, localMax]);
    };

    if (!canRefine) return null;

    const minPercent = ((localMin - minBound) / (maxBound - minBound)) * 100;
    const maxPercent = ((localMax - minBound) / (maxBound - minBound)) * 100;

    return (
        <div className="space-y-4">
            <h3 className="text-xs font-black uppercase text-gray-400 dark:text-gray-500 tracking-widest">
                {t('filters.priceRange')}
            </h3>

            {/* Display Values */}
            <div className="flex items-center justify-between text-sm font-bold text-gray-900 dark:text-white">
                <span>{formatPrice(localMin)}</span>
                <span className="text-gray-200 dark:text-gray-800">—</span>
                <span>{formatPrice(localMax)}</span>
            </div>

            {/* Slider Track */}
            <div className="relative h-2 bg-gray-100 dark:bg-gray-800 rounded-full">
                {/* Active Range */}
                <div
                    className="absolute h-full bg-blue-600 rounded-full shadow-[0_0_15px_-3px_rgba(37,99,235,0.4)]"
                    style={{ left: `${minPercent}%`, right: `${100 - maxPercent}%` }}
                />

                {/* Min Thumb */}
                <input
                    type="range"
                    min={minBound}
                    max={maxBound}
                    value={localMin}
                    onChange={handleMinChange}
                    onMouseUp={handleApply}
                    onTouchEnd={handleApply}
                    className="absolute w-full h-2 appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-blue-600 [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer"
                />

                {/* Max Thumb */}
                <input
                    type="range"
                    min={minBound}
                    max={maxBound}
                    value={localMax}
                    onChange={handleMaxChange}
                    onMouseUp={handleApply}
                    onTouchEnd={handleApply}
                    className="absolute w-full h-2 appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-blue-600 [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer"
                />
            </div>
        </div>
    );
};

// --- Accordion Filter Section ---
const FilterSection = ({
    title,
    children,
    defaultOpen = true
}: {
    title: string,
    children: React.ReactNode,
    defaultOpen?: boolean
}) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
        <div className="border-b border-gray-100 dark:border-gray-800 pb-4">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center justify-between w-full py-2 group"
            >
                <h3 className="text-xs font-black uppercase text-gray-400 dark:text-gray-500 tracking-widest group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                    {title}
                </h3>
                <motion.svg
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    className="w-4 h-4 text-gray-300 dark:text-gray-700"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </motion.svg>
            </button>
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                    >
                        <div className="pt-2">{children}</div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

// --- Pill List Filter ---
const PillListFilter = ({
    attribute,
    label,
    searchable = false,
    singleSelect = false,
    expandable = true
}: {
    attribute: string,
    label: string,
    searchable?: boolean,
    singleSelect?: boolean,
    expandable?: boolean
}) => {
    const { items, refine, searchForItems } = useRefinementList({
        attribute,
        limit: 10,
        showMore: true,
        showMoreLimit: 20,
    });
    const { t } = useTranslation('marketplace');
    const [searchQuery, setSearchQuery] = useState('');

    if (items.length === 0) return null;

    const content = (
        <>
            {searchable && (
                <input
                    type="text"
                    placeholder={t('filters.searchPlaceholder', { label })}
                    value={searchQuery}
                    onChange={(e) => {
                        setSearchQuery(e.target.value);
                        searchForItems(e.target.value);
                    }}
                    className="w-full mb-3 px-3 py-2 text-xs bg-gray-50 dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-xl outline-none focus:border-blue-500/30 font-medium text-gray-900 dark:text-white placeholder:text-gray-400"
                />
            )}
            <div className="flex flex-wrap gap-2">
                {items.map(item => (
                    <PillChip
                        key={item.value}
                        label={item.label}
                        count={item.count}
                        isActive={item.isRefined}
                        onClick={() => {
                            if (singleSelect && !item.isRefined) {
                                items.forEach(i => { if (i.isRefined) refine(i.value); });
                            }
                            refine(item.value);
                        }}
                    />
                ))}
            </div>
        </>
    );

    if (!expandable) {
        return (
            <div className="py-4 space-y-3">
                <h3 className="text-xs font-black uppercase text-gray-400 dark:text-gray-500 tracking-widest">
                    {label}
                </h3>
                {content}
            </div>
        );
    }

    return (
        <FilterSection title={label}>
            {content}
        </FilterSection>
    );
};

// --- Sort Dropdown ---
const SortDropdown = () => {
    const { t } = useTranslation('marketplace');
    const { currentRefinement, options, refine } = useSortBy({
        items: [
            { label: t('filters.sort.default'), value: 'listings' },
            { label: t('filters.sort.priceLowToHigh'), value: 'listings_price_asc' },
            { label: t('filters.sort.priceHighToLow'), value: 'listings_price_desc' },
            { label: t('filters.sort.newest'), value: 'listings_created_at_desc' },
        ],
    });

    return (
        <div className="mb-6">
            <h3 className="text-xs font-black uppercase text-gray-400 dark:text-gray-500 tracking-widest mb-2">
                {t('filters.sortBy')}
            </h3>
            <select
                value={currentRefinement}
                onChange={(e) => refine(e.target.value)}
                className="w-full px-3 py-2.5 bg-white dark:bg-gray-950 border border-gray-100 dark:border-gray-800 rounded-xl text-sm font-semibold text-gray-900 dark:text-white outline-none focus:border-blue-500 transition-colors cursor-pointer appearance-none"
            >
                {options.map(option => (
                    <option key={option.value} value={option.value} className="bg-white dark:bg-gray-950">{option.label}</option>
                ))}
            </select>
        </div>
    );
};

// --- Clear All Button ---
const ClearAllButton = () => {
    const { refine, canRefine } = useClearRefinements();
    const { t } = useTranslation('marketplace');

    if (!canRefine) return null;

    return (
        <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={refine}
            className="w-full py-3 bg-white dark:bg-gray-950 border border-gray-100 dark:border-gray-800 rounded-2xl text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500 hover:text-rose-500 dark:hover:text-rose-400 hover:border-rose-500/20 transition-all flex items-center justify-center gap-2 shadow-sm"
        >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
            {t('filters.clearAll')}
        </motion.button>
    );
};

const HierarchicalCategoryFilters = () => {
    const { items: categories, refine: refineCat } = useRefinementList({ attribute: 'categoryId' });
    const { items: subCategories, refine: refineSub } = useRefinementList({ attribute: 'subCategoryId' });
    const { items: productTypes, refine: refineType } = useRefinementList({ attribute: 'productTypeId' });
    const { t } = useTranslation('marketplace');

    const selectedCategoryId = categories.find(i => i.isRefined)?.value;
    const isSubCategorySelected = subCategories.some(i => i.isRefined);

    // 🛡️ Cleanup: If main category is unselected OR changed, clear children
    useEffect(() => {
        if (!selectedCategoryId) {
            subCategories.forEach(i => { if (i.isRefined) refineSub(i.value); });
            productTypes.forEach(i => { if (i.isRefined) refineType(i.value); });
        }
    }, [selectedCategoryId]);

    return (
        <div className="space-y-4">
            <PillListFilter
                attribute="categoryId"
                label={t('filters.category')}
                singleSelect
                expandable={false}
            />

            {selectedCategoryId && (
                <PillListFilter
                    attribute="subCategoryId"
                    label={t('filters.subCategory')}
                    singleSelect
                    expandable={false}
                />
            )}

            {isSubCategorySelected && (
                <PillListFilter
                    attribute="productTypeId"
                    label={t('filters.productType')}
                    singleSelect
                    expandable={false}
                />
            )}
        </div>
    );
};

// --- Dynamic Attribute Filters ---
const DynamicAttributeFilters = () => {
    // We need to know which category is currently selected to show relevant attributes
    const { items: categories } = useRefinementList({ attribute: 'categoryId' });
    const { items: subCategories } = useRefinementList({ attribute: 'subCategoryId' });
    const { items: productTypes } = useRefinementList({ attribute: 'productTypeId' });
    const { t } = useTranslation('marketplace');

    const selectedCategory = categories.find(i => i.isRefined)?.value;
    const selectedSubCategory = subCategories.find(i => i.isRefined)?.value;
    const selectedProductType = productTypes.find(i => i.isRefined)?.value;

    const attributes = getAttributesForCategory(selectedCategory, selectedSubCategory, selectedProductType);

    if (attributes.length === 0) return null;

    return (
        <div className="pt-4 space-y-2 border-t border-gray-100 dark:border-gray-800">
            <h4 className="text-[10px] font-black uppercase text-gray-400 dark:text-gray-500 tracking-[0.2em] mb-4">
                {t('filters.specifications')}
            </h4>
            {attributes.map(attr => (
                <PillListFilter
                    key={attr.key}
                    attribute={`attributes.${attr.key}`}
                    label={attr.label}
                    searchable={attr.searchable}
                    singleSelect={!attr.multiSelect}
                />
            ))}
        </div>
    );
};
interface FilterSidebarProps {
    className?: string;
}

const FilterSidebar: React.FC<FilterSidebarProps> = ({ className }) => {
    const { items: currentRefinements } = useCurrentRefinements();
    const { items: categories } = useRefinementList({ attribute: 'categoryId' });
    const { query } = useSearchBox();
    const { t } = useTranslation('marketplace');

    const selectedCategory = categories.find(i => i.isRefined)?.value || null;
    const isSearchActive = (query && query.trim().length > 0) || (selectedCategory !== null && selectedCategory !== 'all');

    return (
        <div className={`w-full lg:w-72 flex-shrink-0 space-y-6 ${className}`}>
            {/* Header with Clear Button */}
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-black text-gray-900 dark:text-white">
                    {t('filters.title')}
                </h2>
                {currentRefinements.length > 0 && (
                    <span className="px-2 py-0.5 bg-blue-600/10 text-blue-600 text-[10px] font-bold rounded-full">
                        {currentRefinements.reduce((acc, item) => acc + item.refinements.length, 0)}
                    </span>
                )}
            </div>

            {/* Sort Dropdown - Only show if searching or category selected */}
            {isSearchActive && <SortDropdown />}

            {/* Active Filter Chips */}
            {currentRefinements.length > 0 && (
                <div className="flex flex-wrap gap-2 pb-4 border-b border-gray-100 dark:border-gray-800">
                    {currentRefinements.map(item =>
                        item.refinements.map(ref => (
                            <motion.button
                                key={ref.label}
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.9, opacity: 0 }}
                                onClick={() => item.refine(ref)}
                                className="px-2.5 py-1 bg-blue-600/10 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400 rounded-lg text-[11px] font-black uppercase tracking-tighter flex items-center gap-1.5 hover:bg-blue-600/20 transition-colors"
                            >
                                {ref.label}
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </motion.button>
                        ))
                    )}
                </div>
            )}

            {/* Filter Sections */}
            <div className="space-y-2">
                <HierarchicalCategoryFilters />
                <PriceRangeSlider />
                <PillListFilter attribute="condition" label={t('filters.condition')} />
                <PillListFilter attribute="city" label={t('filters.city')} searchable />
                <PillListFilter attribute="district" label={t('filters.district')} searchable />
            </div>

            {/* Dynamic Category-Specific Filters */}
            <DynamicAttributeFilters />

            {/* Clear All Button */}
            <div className="pt-4">
                <ClearAllButton />
            </div>
        </div>
    );
};

export default FilterSidebar;
